import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { IoInformationCircleOutline } from 'react-icons/io5';

const VIEWPORT_MARGIN = 12;
const HARD_MAX = 320;

/**
 * InfoTooltip — 클릭으로 토글, 외부 클릭 시 닫힘.
 * position:fixed + viewport 좌표 직접 박음 → ancestor transform / overflow 영향 X.
 * 측정 전엔 visibility:hidden 으로 깜빡임 차단.
 */
const InfoTooltip = ({ text }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const bubbleRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('touchstart', handler);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !ref.current || !bubbleRef.current) return;
    const bubble = bubbleRef.current;
    const vw = window.innerWidth;
    const maxW = Math.min(HARD_MAX, vw - 2 * VIEWPORT_MARGIN);

    bubble.style.maxWidth = `${maxW}px`;
    bubble.style.visibility = 'hidden';

    const raf = requestAnimationFrame(() => {
      if (!ref.current || !bubbleRef.current) return;
      const iconRect = ref.current.getBoundingClientRect();
      const bRect = bubbleRef.current.getBoundingClientRect();
      const w = bRect.width;
      const top = iconRect.bottom + 8;
      let left = iconRect.left + iconRect.width / 2 - w / 2;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
      if (left + w > vw - VIEWPORT_MARGIN) left = vw - VIEWPORT_MARGIN - w;
      bubbleRef.current.style.top = `${top}px`;
      bubbleRef.current.style.left = `${left}px`;
      bubbleRef.current.style.visibility = 'visible';
    });

    return () => cancelAnimationFrame(raf);
  }, [open, text]);

  return (
    <Wrap ref={ref}>
      <IconBtn onClick={() => setOpen((v) => !v)}>
        <IoInformationCircleOutline size={18} />
      </IconBtn>
      {open && <Bubble ref={bubbleRef}>{text}</Bubble>}
    </Wrap>
  );
};

export default InfoTooltip;

const Wrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  vertical-align: middle;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
`;

const Bubble = styled.div`
  position: fixed;
  top: -9999px;
  left: -9999px;
  width: max-content;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.text};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 1.6;
  border-radius: ${({ theme }) => theme.radius.md};
  z-index: 10000;
  white-space: pre-line;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  visibility: hidden; /* JS 가 측정 후 visible 로 전환 */
`;
